/**
 * Database Types for Secondary Market Fund Dashboard
 * Aligned with Cloudflare D1 schema
 */

// ============================================================================
// ENUMS
// ============================================================================

export const DealStage = {
  SOURCED: 'sourced',
  LOI_SENT: 'loi_sent',
  DUE_DILIGENCE: 'due_diligence',
  GP_CONSENT: 'gp_consent',
  DOCUMENTATION: 'documentation',
  CLOSING: 'closing',
  CLOSED: 'closed',
  ARCHIVED: 'archived',
} as const;

export type DealStageType = (typeof DealStage)[keyof typeof DealStage];

export const DealTemperature = {
  HOT: 'hot',
  WARM: 'warm',
  COLD: 'cold',
} as const;

export type DealTemperatureType = (typeof DealTemperature)[keyof typeof DealTemperature];

export const WorkflowStatus = {
  PENDING: 'pending',
  RUNNING: 'running',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type WorkflowStatusType = (typeof WorkflowStatus)[keyof typeof WorkflowStatus];

// NEW: Transaction Structure Types (v1.1 - March 2026)
export const TransactionStructure = {
  WHOLE_LP_INTEREST: 'whole_lp_interest',
  PARTIAL_STRIP: 'partial_strip',
  MULTI_FUND_PORTFOLIO: 'multi_fund_portfolio',
} as const;

export type TransactionStructureType = (typeof TransactionStructure)[keyof typeof TransactionStructure];

export const GPConsentStatus = {
  PENDING: 'pending',
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  ROFR_ACTIVE: 'rofr_active',
  ROFR_EXERCISED: 'rofr_exercised',
  DENIED: 'denied',
} as const;

export type GPConsentStatusType = (typeof GPConsentStatus)[keyof typeof GPConsentStatus];

export const CVParticipationStatus = {
  MONITORING: 'monitoring',
  INTERESTED: 'interested',
  COMMITTED: 'committed',
  INVESTED: 'invested',
  PASSED: 'passed',
} as const;

export type CVParticipationStatusType = (typeof CVParticipationStatus)[keyof typeof CVParticipationStatus];

// ============================================================================
// TABLE TYPES
// ============================================================================

export interface Fund {
  id: string;
  name: string;
  vintage_year: number;
  fund_size: number;
  gp_name: string;
  remaining_term_years: number | null;
  management_fee: number;
  carry_rate: number;
  created_at: string;
}

export interface Deal {
  id: string;
  name: string;
  fund_id: string | null;
  seller_name: string;
  seller_email: string | null;
  stage: DealStageType;
  target_value: number;
  headline_discount: number | null;
  true_discount: number | null;
  fee_drag: number | null;
  temperature: DealTemperatureType;
  next_action: string | null;
  notes: string | null;
  owner: string;
  created_at: string;
  updated_at: string;
  // NEW: Transaction structure fields (v1.1)
  transaction_structure: TransactionStructureType;
  partial_percentage: number | null;  // For partial strips: 0.25-0.50
  parent_deal_id: string | null;  // For multi-fund portfolios
  cv_opportunity: CVOpportunity | null;
  cv_participation_status: CVParticipationStatusType | null;
  // NEW: Prioritization fields (v1.2 - Ed's dashboard requirements)
  closing_probability: number | null;  // 0-100 percentage
  estimated_close_date: string | null;  // ISO date string
  concern_areas: string | null;  // Comma-separated: "gp_resistance,legal_complexity,pricing"
}

export interface CVOpportunity {
  cv_name: string;
  gp_name: string;
  expected_close: string;
  participation_interest: number;
}

export interface DealWithFund extends Deal {
  fund: Fund | null;
}

// NEW: Multi-Fund Portfolio Types (v1.1)
export interface PortfolioDeal {
  id: string;
  name: string;
  seller_name: string;
  seller_email: string | null;
  total_nav: number;
  blended_discount: number | null;
  blended_true_discount: number | null;
  total_fee_drag: number | null;
  fund_count: number;
  status: PortfolioStatusType;
  close_method: 'simultaneous' | 'sequential';
  notes: string | null;
  owner: string;
  created_at: string;
  updated_at: string;
}

export const PortfolioStatus = {
  SOURCED: 'sourced',
  VALUATION: 'valuation',
  NEGOTIATION: 'negotiation',
  GP_CONSENT: 'gp_consent',
  DOCUMENTATION: 'documentation',
  CLOSING: 'closing',
  CLOSED: 'closed',
  ARCHIVED: 'archived',
} as const;

export type PortfolioStatusType = (typeof PortfolioStatus)[keyof typeof PortfolioStatus];

export interface PortfolioFundPosition {
  id: string;
  portfolio_id: string;
  deal_id: string;
  fund_id: string | null;
  position_nav: number;
  individual_discount: number | null;
  individual_true_discount: number | null;
  individual_fee_drag: number | null;
  gp_consent_status: GPConsentStatusType;
  weight_in_portfolio: number | null;
  created_at: string;
}

export interface PortfolioDealWithPositions extends PortfolioDeal {
  positions: PortfolioFundPosition[];
  deals: DealWithFund[];
}

export interface WorkflowExecution {
  id: string;
  workflow_name: string;
  status: WorkflowStatusType;
  parameters: Record<string, unknown> | null;
  result: Record<string, unknown> | null;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  triggered_by: string;
}

export interface ActivityLog {
  id: string;
  deal_id: string | null;
  action: string;
  description: string;
  actor: string;
  created_at: string;
}

export interface UserSettings {
  user_id: string;
  settings: {
    default_currency: 'USD' | 'EUR' | 'GBP';
    target_true_discount_min: number;
    target_true_discount_max: number;
    fee_drag_warning_threshold: number;
    theme: 'light' | 'dark' | 'system';
  };
  updated_at: string;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DashboardStats {
  activeDeals: number;
  pipelineValue: number;
  closedThisMonth: number;
  avgTrueDiscount: number;
  dealsByStage: Record<DealStageType, number>;
  dealsByTemperature: Record<DealTemperatureType, number>;
}

// ============================================================================
// FORM INPUT TYPES
// ============================================================================

export interface CreateDealInput {
  name: string;
  fund_id?: string;
  seller_name: string;
  seller_email?: string;
  target_value: number;
  headline_discount?: number;
  temperature?: DealTemperatureType;
  notes?: string;
  // NEW: Transaction structure (v1.1)
  transaction_structure?: TransactionStructureType;
  partial_percentage?: number;
  parent_deal_id?: string;
  // NEW: Prioritization fields (v1.2)
  closing_probability?: number;
  estimated_close_date?: string;
  concern_areas?: string;
}

export interface UpdateDealInput {
  name?: string;
  fund_id?: string;
  seller_name?: string;
  seller_email?: string;
  stage?: DealStageType;
  target_value?: number;
  headline_discount?: number;
  true_discount?: number;
  fee_drag?: number;
  temperature?: DealTemperatureType;
  next_action?: string;
  notes?: string;
  // NEW: Prioritization fields (v1.2)
  closing_probability?: number;
  estimated_close_date?: string;
  concern_areas?: string;
}

export interface RunWorkflowInput {
  workflow_name: string;
  parameters?: Record<string, unknown>;
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

export const stageLabels: Record<DealStageType, string> = {
  [DealStage.SOURCED]: 'Sourced',
  [DealStage.LOI_SENT]: 'LOI Sent',
  [DealStage.DUE_DILIGENCE]: 'Due Diligence',
  [DealStage.GP_CONSENT]: 'GP Consent',
  [DealStage.DOCUMENTATION]: 'Documentation',
  [DealStage.CLOSING]: 'Closing',
  [DealStage.CLOSED]: 'Closed',
  [DealStage.ARCHIVED]: 'Archived',
};

export const stageColors: Record<DealStageType, string> = {
  [DealStage.SOURCED]: 'badge-sourced',
  [DealStage.LOI_SENT]: 'badge-loi',
  [DealStage.DUE_DILIGENCE]: 'badge-dd',
  [DealStage.GP_CONSENT]: 'badge-consent',
  [DealStage.DOCUMENTATION]: 'badge-documentation',
  [DealStage.CLOSING]: 'badge-closing',
  [DealStage.CLOSED]: 'badge-closed',
  [DealStage.ARCHIVED]: 'badge-cold',
};

export const temperatureLabels: Record<DealTemperatureType, string> = {
  [DealTemperature.HOT]: 'Hot',
  [DealTemperature.WARM]: 'Warm',
  [DealTemperature.COLD]: 'Cold',
};

export const temperatureColors: Record<DealTemperatureType, string> = {
  [DealTemperature.HOT]: 'badge-hot',
  [DealTemperature.WARM]: 'badge-warm',
  [DealTemperature.COLD]: 'badge-cold',
};

export const workflowStatusColors: Record<WorkflowStatusType, string> = {
  [WorkflowStatus.PENDING]: 'text-gray-500',
  [WorkflowStatus.RUNNING]: 'text-blue-500',
  [WorkflowStatus.COMPLETED]: 'text-green-500',
  [WorkflowStatus.FAILED]: 'text-red-500',
};

// NEW: Transaction Structure Display Helpers (v1.1)
export const transactionStructureLabels: Record<TransactionStructureType, string> = {
  [TransactionStructure.WHOLE_LP_INTEREST]: 'Whole LP Interest',
  [TransactionStructure.PARTIAL_STRIP]: 'Partial Strip',
  [TransactionStructure.MULTI_FUND_PORTFOLIO]: 'Multi-Fund Portfolio',
};

export const transactionStructureIcons: Record<TransactionStructureType, string> = {
  [TransactionStructure.WHOLE_LP_INTEREST]: '📄',
  [TransactionStructure.PARTIAL_STRIP]: '✂️',
  [TransactionStructure.MULTI_FUND_PORTFOLIO]: '📦',
};

export const transactionStructureDescriptions: Record<TransactionStructureType, string> = {
  [TransactionStructure.WHOLE_LP_INTEREST]: 'Full transfer of LP interest to buyer',
  [TransactionStructure.PARTIAL_STRIP]: 'Transfer of 25-50% of LP interest',
  [TransactionStructure.MULTI_FUND_PORTFOLIO]: 'Bundled interests across 3-5 funds',
};

export const gpConsentStatusLabels: Record<GPConsentStatusType, string> = {
  [GPConsentStatus.PENDING]: 'Pending',
  [GPConsentStatus.SUBMITTED]: 'Submitted',
  [GPConsentStatus.APPROVED]: 'Approved',
  [GPConsentStatus.ROFR_ACTIVE]: 'ROFR Active',
  [GPConsentStatus.ROFR_EXERCISED]: 'ROFR Exercised',
  [GPConsentStatus.DENIED]: 'Denied',
};

export const gpConsentStatusColors: Record<GPConsentStatusType, string> = {
  [GPConsentStatus.PENDING]: 'text-gray-500',
  [GPConsentStatus.SUBMITTED]: 'text-blue-500',
  [GPConsentStatus.APPROVED]: 'text-green-500',
  [GPConsentStatus.ROFR_ACTIVE]: 'text-yellow-500',
  [GPConsentStatus.ROFR_EXERCISED]: 'text-orange-500',
  [GPConsentStatus.DENIED]: 'text-red-500',
};

// NEW: Concern Areas (v1.2 - Ed's dashboard requirements)
export const ConcernArea = {
  GP_RESISTANCE: 'gp_resistance',
  LEGAL_COMPLEXITY: 'legal_complexity',
  PRICING_DISAGREEMENT: 'pricing_disagreement',
  LP_UNRESPONSIVE: 'lp_unresponsive',
  ROFR_RISK: 'rofr_risk',
  DOCUMENTATION_DELAYS: 'documentation_delays',
} as const;

export type ConcernAreaType = (typeof ConcernArea)[keyof typeof ConcernArea];

export const concernAreaLabels: Record<ConcernAreaType, string> = {
  [ConcernArea.GP_RESISTANCE]: 'GP Resistance',
  [ConcernArea.LEGAL_COMPLEXITY]: 'Legal Complexity',
  [ConcernArea.PRICING_DISAGREEMENT]: 'Pricing Disagreement',
  [ConcernArea.LP_UNRESPONSIVE]: 'LP Unresponsive',
  [ConcernArea.ROFR_RISK]: 'ROFR Risk',
  [ConcernArea.DOCUMENTATION_DELAYS]: 'Documentation Delays',
};

// Helper to get priority color based on deal metrics
export function getDealPriorityColor(deal: Deal): 'green' | 'yellow' | 'red' {
  const prob = deal.closing_probability ?? 50;
  const value = deal.target_value;
  const hasConcerns = deal.concern_areas && deal.concern_areas.length > 0;

  // Red flags
  if (prob < 40 || hasConcerns) return 'red';
  // Green flags
  if (prob >= 70 && value >= 1000000) return 'green';
  // Yellow - everything else
  return 'yellow';
}

// Helper to get days until close
export function getDaysUntilClose(deal: Deal): number | null {
  if (!deal.estimated_close_date) return null;
  const closeDate = new Date(deal.estimated_close_date);
  const today = new Date();
  const diffTime = closeDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// Helper to get timeline color
export function getTimelineColor(daysUntilClose: number | null): 'green' | 'yellow' | 'red' {
  if (daysUntilClose === null) return 'yellow';
  if (daysUntilClose < 30) return 'green';
  if (daysUntilClose <= 90) return 'yellow';
  return 'red';
}
